import { useEffect, useMemo, useState } from "react";
import { ArrowUp, ArrowDown, X, Plus, Trash2, GripVertical, Star } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { FAV_COLORS, favPages } from "@/lib/favorites";
import { fmtNum, uuid, vals } from "@/lib/format";
import { inputCls } from "@/components/ui-kit";

const move = (arr, from, to) => { const a = [...arr]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; };

function ItemRow({ item, i, n, product, onPatch, onMove, onRemove, drag }) {
  return (
    <div draggable onDragStart={() => drag.start(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drag.drop(i)}
      className="flex flex-wrap items-center gap-2 border border-border rounded-lg p-2 bg-white" data-testid="fav-item-row">
      <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
      <span className="flex-1 min-w-[120px] font-bold text-sm truncate px-2 py-1 rounded" style={{ background: item.color }}>{product.name} <span className="font-normal" dir="ltr">{fmtNum(product.price)}</span></span>
      <span className="flex gap-1">{FAV_COLORS.map((c) => (
        <button key={c} type="button" onClick={() => onPatch({ color: c })} aria-label={c} data-testid="fav-color-swatch"
          className={`w-6 h-6 rounded-full border ${item.color === c ? "ring-2 ring-[#0B2A5B] border-white" : "border-[#C9D3E0]"}`} style={{ background: c }} />
      ))}</span>
      <button type="button" disabled={i === 0} onClick={() => onMove(i - 1)} className="w-8 h-8 grid place-items-center rounded bg-secondary disabled:opacity-30" data-testid="fav-item-up"><ArrowUp className="w-4 h-4" /></button>
      <button type="button" disabled={i === n - 1} onClick={() => onMove(i + 1)} className="w-8 h-8 grid place-items-center rounded bg-secondary disabled:opacity-30" data-testid="fav-item-down"><ArrowDown className="w-4 h-4" /></button>
      <button type="button" onClick={onRemove} className="w-8 h-8 grid place-items-center rounded bg-destructive/10 text-destructive" data-testid="fav-item-remove"><X className="w-4 h-4" /></button>
    </div>
  );
}

export default function FavEditor({ open, onClose }) {
  const { t, view, dispatch } = useApp();
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [newName, setNewName] = useState("");
  const [q, setQ] = useState("");
  const [dragFrom, setDragFrom] = useState(null);
  useEffect(() => {
    if (!open) return;
    const p = JSON.parse(JSON.stringify(favPages(view)));
    setPages(p); setActive(p[0]?.id || null); setQ("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const page = pages.find((p) => p.id === active);
  const setPage = (patch) => setPages((ps) => ps.map((p) => (p.id === active ? { ...p, ...patch } : p)));
  const setItems = (items) => setPage({ items });
  const addPage = () => {
    const name = newName.trim();
    if (!name) return;
    const p = { id: uuid(), name, items: [] };
    setPages((ps) => [...ps, p]); setActive(p.id); setNewName("");
  };
  const candidates = useMemo(() => vals(view.products)
    .filter((p) => !p.deleted && !page?.items.some((i) => i.product_id === p.id) && `${p.name} ${p.barcode || ""}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name)).slice(0, 12), [view.products, page, q]);
  const save = () => {
    if (!dispatch("favorites.update", { pages: pages.map((p) => ({ ...p, name: p.name.trim() || "—" })) })) return;
    toast.success(t("fav_saved")); onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[94vh] overflow-y-auto" data-testid="fav-editor">
        <DialogTitle className="font-heading text-xl flex items-center gap-2"><Star className="w-5 h-5" />{t("fav_edit")}</DialogTitle>
        <div className="flex flex-wrap gap-2">
          {pages.map((p) => (
            <button key={p.id} type="button" onClick={() => setActive(p.id)} data-testid="fav-page-tab"
              className={`h-10 px-3 rounded-lg border text-sm font-bold ${p.id === active ? "bg-[#0B2A5B] text-white border-[#0B2A5B]" : "bg-white border-border"}`}>{p.name} ({p.items.length})</button>
          ))}
          <span className="flex gap-1">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPage()} placeholder={t("fav_new_page")} className="h-10 w-36 rounded-lg border border-border px-2 text-sm" data-testid="fav-new-page-input" />
            <button type="button" onClick={addPage} className="h-10 w-10 rounded-lg bg-secondary grid place-items-center" data-testid="fav-add-page-btn"><Plus className="w-4 h-4" /></button>
          </span>
        </div>
        {page && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input value={page.name} onChange={(e) => setPage({ name: e.target.value })} aria-label={t("fav_page_name")} className={inputCls} data-testid="fav-page-name-input" />
              <button type="button" onClick={() => { const rest = pages.filter((p) => p.id !== active); setPages(rest); setActive(rest[0]?.id || null); }}
                className="h-12 px-3 rounded-lg bg-destructive/10 text-destructive text-sm font-bold flex items-center gap-1 shrink-0" data-testid="fav-delete-page-btn"><Trash2 className="w-4 h-4" />{t("fav_delete_page")}</button>
            </div>
            <div className="space-y-1.5" data-testid="fav-items">
              {page.items.map((it, i) => {
                const product = view.products?.[it.product_id];
                if (!product) return null;
                return <ItemRow key={it.product_id} item={it} i={i} n={page.items.length} product={product}
                  onPatch={(patch) => setItems(page.items.map((x, j) => (j === i ? { ...x, ...patch } : x)))}
                  onMove={(to) => setItems(move(page.items, i, to))} onRemove={() => setItems(page.items.filter((_, j) => j !== i))}
                  drag={{ start: setDragFrom, drop: (to) => { if (dragFrom !== null && dragFrom !== to) setItems(move(page.items, dragFrom, to)); setDragFrom(null); } }} />;
              })}
            </div>
            <div className="rounded-lg bg-secondary p-2 space-y-2">
              <div className="text-sm font-bold">{t("fav_add_products")}</div>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_products")} className={inputCls} data-testid="fav-product-search" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {candidates.map((p) => (
                  <button key={p.id} type="button" onClick={() => setItems([...page.items, { product_id: p.id, color: FAV_COLORS[page.items.length % FAV_COLORS.length] }])}
                    className="h-11 px-2 rounded-lg bg-white border border-border text-xs font-bold flex items-center gap-1 text-start" data-testid="fav-add-product-btn"><Plus className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{p.name}</span></button>
                ))}
              </div>
            </div>
          </div>
        )}
        <button type="button" onClick={save} className="h-12 bg-[#0B2A5B] text-white font-bold rounded-lg" data-testid="fav-save-btn">{t("save")}</button>
      </DialogContent>
    </Dialog>
  );
}
